import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMfaEnrollment, usePrivy } from '@privy-io/react-auth';
import Navbar, { VETS_PATHS } from '../views/Gifts/Navbar';
import { selectPrivyToken, setPrivyToken } from '../redux/slices/auth';
import { useEffect, useState } from 'react';
import {
  useSetReferralMutation,
  useSetUtmDataMutation,
} from '../redux/api/authorized/auth';
import { useSelector, useDispatch } from 'react-redux';
import WidthContainer from '../components/WidthContainer';
import { useSolana } from '../hooks/useSolana';
import { useGetDefaultTokensQuery } from '../redux/api/public/tokens';
import Footer from '../components/Footer';
import { UNLEASH_ENABLED_BUY_VETS } from '../constants';
import { useFlag } from '@unleash/proxy-client-react';
import { IonPage, IonContent, IonHeader, IonToolbar } from '@ionic/react';
import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../common/responsive';
import { Capacitor } from '@capacitor/core';
import { useListPrepaidCardsQuery } from '../redux/api/authorized/zyptoPrepaidCards';

const Container = styled.div`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  @media ${PHONE_MEDIA_QUERY} {
    min-height: calc(100vh - 76px - 56px);
    ${() =>
      Capacitor.isNativePlatform() &&
      `
        min-height: calc(100vh - 60px - 56px - env(safe-area-inset-bottom) - env(safe-area-inset-top));
      `}
  }
`;

// Helper function to check if user is newly created (within the last hour)
const isNewlyCreated = (userToCheck) => {
  if (!userToCheck?.createdAt) return false;

  try {
    const createdAt = new Date(userToCheck.createdAt);
    const now = new Date();
    const hoursSinceCreation =
      (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

    // Consider user "new" if created within the last hour
    return hoursSinceCreation < 1;
  } catch (error) {
    console.error('Error parsing user createdAt:', error);
    return false;
  }
};

const AppLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMfaEnrollmentModal } = useMfaEnrollment();
  const { data: prepaidCards } = useListPrepaidCardsQuery();
  const [mfaShown, setMfaShown] = useState(() => {
    return localStorage.getItem('mfaShown') || 'false';
  });
  const { authenticated, user, ready } = usePrivy();
  const enableBuyVets = useFlag(UNLEASH_ENABLED_BUY_VETS);

  const { refetch } = useGetDefaultTokensQuery();

  let excludeFooter = false;

  useEffect(() => {
    localStorage.setItem('mfaShown', mfaShown);
  }, [mfaShown]);

  useEffect(() => {
    if (enableBuyVets) {
      refetch();
    }
  }, [enableBuyVets, refetch]);

  useEffect(() => {
    if (user) {
      if (user.mfaMethods?.length === 0 && mfaShown === 'false') {
        //showMfaEnrollmentModal();
        //setMfaShown('true');
      }
    }
  }, [user, mfaShown]);

  const privyToken = useSelector(selectPrivyToken);

  const { refreshTokenBalances, isRefreshing, lastRefreshTime } = useSolana();

  const [setReferral] = useSetReferralMutation();
  const [setUtmData] = useSetUtmDataMutation();

  useEffect(() => {
    if (ready && lastRefreshTime === 0 && !isRefreshing) {
      refreshTokenBalances();
    }
  }, [lastRefreshTime, isRefreshing, ready]);

  // Capture UTM parameters from URL or referrer and store in localStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');

    // Priority 1: Capture UTM parameters from URL if they exist
    if (utmSource || utmMedium || utmCampaign) {
      const utmData = {
        utm_source: utmSource || null,
        utm_medium: utmMedium || null,
        utm_campaign: utmCampaign || null,
      };
      localStorage.setItem('deks_utm_data', JSON.stringify(utmData));
      return;
    }

    // Priority 2: For newly created users, capture referrer if no UTM parameters exist
    if (user && ready) {
      // Check if we've already stored UTM data
      const existingUtmData = localStorage.getItem('deks_utm_data');
      if (existingUtmData) return;

      if (isNewlyCreated(user)) {
        // Check if we've already captured referrer for this user
        const referrerCapturedKey = `deks_referrer_captured_${user.id}`;
        if (localStorage.getItem(referrerCapturedKey)) return;

        // Get document referrer
        const referrer = document.referrer;

        // Only store if referrer exists and is not the same domain
        if (referrer) {
          try {
            const referrerUrl = new URL(referrer);
            const currentUrl = new URL(window.location.href);

            // Only store if referrer is from a different domain
            if (referrerUrl.hostname !== currentUrl.hostname) {
              const utmData = {
                utm_source: referrer,
                utm_medium: null,
                utm_campaign: null,
              };
              localStorage.setItem('deks_utm_data', JSON.stringify(utmData));
              // Mark that we've captured referrer for this user
              localStorage.setItem(referrerCapturedKey, 'true');
            }
          } catch (error) {
            // If referrer is not a valid URL, still try to store it
            const utmData = {
              utm_source: referrer,
              utm_medium: null,
              utm_campaign: null,
            };
            localStorage.setItem('deks_utm_data', JSON.stringify(utmData));
            localStorage.setItem(referrerCapturedKey, 'true');
          }
        }
      }
    }
  }, [location.search, user, ready]);

  useEffect(() => {
    if (user && privyToken) {
      const referralCode = localStorage.getItem('rc');
      if (referralCode) {
        const referredUser = localStorage.getItem('referredUserId');
        if (referredUser !== String(user.id)) {
          const handleReferral = async () => {
            try {
              await setReferral(referralCode).unwrap();
              localStorage.setItem('referredUserId', String(user.id));
              localStorage.removeItem('rc');
            } catch (error) {
              if (error?.status === 404) {
                // This error is returned by the API when the privy user is not created in the DB yet
                return;
              }
              // For other errors, clear the local storage to prevent infinite retries
              localStorage.setItem('referredUserId', String(user.id));
              localStorage.removeItem('rc');
            }
          };
          handleReferral();
        } else {
          localStorage.removeItem('rc');
        }
      }

      // Send UTM data to backend on login if stored (only for newly created users)
      const handleUtmData = async () => {
        const storedUtmData = localStorage.getItem('deks_utm_data');
        if (storedUtmData) {
          // Only send UTM data for newly created users
          if (isNewlyCreated(user)) {
            try {
              const utmData = JSON.parse(storedUtmData);
              await setUtmData(utmData).unwrap();
              // Clear after successful send
              localStorage.removeItem('deks_utm_data');
            } catch (error) {
              // Log error but don't block user flow
              console.error('Error sending UTM data:', error);
              // Still clear data to prevent infinite retries
              localStorage.removeItem('deks_utm_data');
            }
          } else {
            // Clear UTM data if user is not newly created
            localStorage.removeItem('deks_utm_data');
          }
        }
      };
      handleUtmData();
    }
  }, [user, privyToken, setReferral, setUtmData]);

  VETS_PATHS.forEach((vetsPath) => {
    if (location.pathname.includes(vetsPath)) {
      excludeFooter = true;
    }
  });

  // Exclude footer for gift flow create page
  if (location.pathname.includes('/app/gifts/create')) {
    excludeFooter = true;
  }

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const PageContainer = isMobile ? IonPage : 'div';

  const Content = isMobile ? IonContent : 'div';

  return (
    <PageContainer className='app-layout' id='main-content'>
      {/* If you want the Navbar fixed, put it in IonHeader.
          If it's your custom component, just render it here. */}
      {isMobile ? (
        <IonHeader>
          <IonToolbar
            style={{
              '--border-color': theme.text.disabled,
              '--min-height': 'auto',
              '--background': theme.background.primary,
            }}
          >
            <Navbar />
          </IonToolbar>
        </IonHeader>
      ) : (
        <Navbar />
      )}

      <Content style={{ '--background': theme.background.primary }}>
        <WidthContainer>
          <Container>
            <Outlet />
          </Container>
          {!excludeFooter && <Footer />}
        </WidthContainer>
      </Content>
    </PageContainer>
  );
};

export default AppLayout;
